'use client';

import { useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';

import { KINDE_MIGRATION_MODAL_ID } from '@/app-shell/modal-queue.types';
import { useQueryAppleMigrationTicket } from '@/hooks/query/useQueryAppleMigrationTicket';
import { useQueuedModal } from '@/hooks/useQueuedModal';
import { logout } from '@/lib/authActions';
import { useDesktopAuthStore } from '@/models/desktopAuthSlice';
import { launchDesktopAuthLogin } from '@/tauri/auth0/login';
import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import {
  AUTH0_APPLE_CONNECTION,
  AUTH0_APPLE_MIGRATION_STATUS_PATH,
  AUTH0_LOGIN_PATH,
} from '@heidi/auth/contracts';
import { Button } from '@heidi/design-system/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@heidi/design-system/dialog';
import { TypographyH2, TypographyP2 } from '@heidi/design-system/typography';
import { toAuth0UiLocales } from '@heidi/utils/i18n';

import { useKindeMigrationCompletion } from './useKindeMigrationCompletion';

/** Build the Auth0 Apple login URL, mirroring AppleLoginButton's pattern. */
const buildAuth0AppleLoginUrl = (
  returnTo: string,
  ticketId: string | null,
  locale: string | null
): string => {
  const params = new URLSearchParams();
  params.set('returnTo', returnTo);
  params.set('connection', AUTH0_APPLE_CONNECTION);
  // Carry the active UI locale so the Auth0 hosted login page renders in the
  // user's language, matching AppleLoginButton's canonical builder.
  if (locale) {
    params.set('ui_locales', toAuth0UiLocales(locale));
  }
  // The Auth0 nextjs SDK forwards all non-`returnTo` query params to
  // `/authorize`, so the Auth0 post-login Action reads this ticket via
  // `event.request.query.migration_ticket_id`. Param name aligned with the
  // backend consume-ticket Action.
  if (ticketId) {
    params.set('migration_ticket_id', ticketId);
  }
  return `${AUTH0_LOGIN_PATH}?${params.toString()}`;
};

/**
 * Forced (non-dismissible) full-screen takeover that prompts Kinde users to
 * migrate to Auth0. Enqueued via the launch queue (KINDE_MIGRATION_MODAL_ID)
 * when `useQueryAppleMigrationTicket` returns a live ticket. Built on the
 * design-system `Dialog` with `dismissible={false}` + `hideCloseIcon` (the
 * canonical forced-modal pattern) so it keeps the focus trap, `role="dialog"`/
 * `aria-modal`, and background inerting — the branded Auth0 sign-in backdrop is
 * applied to the modal overlay so it fills the screen behind the card. The open
 * state is controlled solely by the queue (no `onOpenChange`), so the user
 * cannot dismiss it via Esc or outside-click. "Continue" opens the Auth0 Apple
 * login (with the minted `ticket_id`) in a NEW tab; the original tab watches for
 * completion and the takeover auto-dismisses once `authProvider` flips to AUTH0.
 * "Sign in another way" logs the user out so they can re-authenticate any way.
 */
export const KindeMigrationModal = () => {
  const intl = useIntl();
  const { isOpen } = useQueuedModal(KINDE_MIGRATION_MODAL_ID);
  // Same query key as GlobalModalsWrapper → cache hit, no extra call. Reads the
  // minted ticket to hand to Auth0. `keepFresh` re-mints on an interval while
  // the takeover is open so the ticket never expires (~15 min) before confirm.
  const { data: appleMigration } = useQueryAppleMigrationTicket({ keepFresh: isOpen });

  // Web-only: watch for new-tab login completion via the shared-cookie flip
  // while the modal is open. Desktop completes through the Tauri plugin's
  // `auth:login-success` event (handled in `src/tauri/auth0/provider.tsx`), so
  // there is no tab/cookie to poll there.
  useKindeMigrationCompletion(isOpen && !isTauri());

  const ticketId = appleMigration?.ticket_id ?? null;
  // Short-lived guard against a rapid double-tap of confirm on desktop. Unlike
  // the persistent `migrationInProgress` marker, it is always released in
  // `finally`, so an abandoned browser login (which emits no Tauri event) never
  // permanently blocks a retry.
  const launchInFlightRef = useRef(false);
  const handleContinue = useCallback(() => {
    if (isTauri()) {
      // Desktop: complete via the Tauri plugin (system browser + custom-scheme
      // deep link), not a new browser tab.
      const desktopAuthStore = useDesktopAuthStore.getState();

      // Guard before launching:
      // 1. Never start the migration without a minted ticket. Launching Apple
      //    login with no `migration_ticket_id` would let Auth0 create an orphan
      //    account, and the subsequent `auth:login-success` would then revoke the
      //    Kinde session with no real migration link — a revoked-but-unmigrated
      //    state (the ticket can be transiently null if the eligibility query
      //    re-mints to a non-ticket status as the user clicks).
      // 2. Ignore a rapid double-tap via the SHORT-LIVED `launchInFlightRef`,
      //    which only covers the brief launch window and is always cleared in
      //    `finally`. We must NOT gate on the persistent `migrationInProgress`
      //    marker here: it is cleared only by a Tauri event, so if the user
      //    abandons/closes the browser (no event) gating on it would leave the
      //    forced modal's confirm button a permanent no-op until reload. Rust
      //    replaces any pending attempt on a fresh launch, so a retry is safe.
      if (!ticketId || launchInFlightRef.current) {
        return;
      }
      launchInFlightRef.current = true;

      // Mark the migration in flight so `handleLoginSuccess` revokes the old
      // Kinde session *before* it swaps the Redux token, then hand the minted
      // ticket to the plugin — appended to `/authorize` as `migration_ticket_id`
      // for the Auth0 Post-Login Action.
      desktopAuthStore.setMigrationInProgress();
      void launchDesktopAuthLogin({
        connection: AUTH0_APPLE_CONNECTION,
        migrationTicket: ticketId,
        uiLocales: toAuth0UiLocales(intl.locale),
      })
        .catch((error) => {
          // Launch failed before the browser opened — clear the marker so a
          // later normal login isn't mis-classified as a migration completion.
          desktopAuthStore.clearMigrationInProgress();
          logger.error('[KindeMigrationModal] Desktop migration login failed', error);
        })
        .finally(() => {
          // Release the double-tap guard once the launch has settled (opened or
          // failed). The persistent `migrationInProgress` marker is left intact
          // for the completion handler; a later retry is allowed via this ref.
          launchInFlightRef.current = false;
        });
      return;
    }
    // Web: open Auth0 Apple login in a NEW tab. `returnTo` lands the new tab on
    // the migration status page; `onCallback` appends `?migration=success|failed`
    // so the page can render the outcome, and the original tab auto-dismisses
    // this modal once `authProvider` flips to AUTH0.
    const loginUrl = buildAuth0AppleLoginUrl(
      AUTH0_APPLE_MIGRATION_STATUS_PATH,
      ticketId,
      intl.locale
    );
    window.open(loginUrl, '_blank');
  }, [ticketId, intl.locale]);

  // "Sign in another way": for now, log the user out so they can re-authenticate
  // via any method. (Kinde users go through the full logout + redirect.)
  const handleSignInAnotherWay = useCallback(() => {
    void logout();
  }, []);

  return (
    <Dialog open={isOpen}>
      <DialogContent
        dismissible={false}
        hideCloseIcon
        className="max-w-md"
        // Brand-asset backdrop on the full-screen overlay: solid surface-page
        // fill (sand/25 = #FCFAF8) overriding the default dark dialog overlay,
        // with the brand line artwork layered on top (no design token exists
        // for a background image; intentional arbitrary value).
        overlayClassName="bg-surface-page bg-[url('/images/auth0-signin-background.png')] bg-cover bg-center bg-no-repeat"
      >
        <DialogHeader className="items-center gap-2 text-center">
          <DialogTitle asChild>
            <TypographyH2>
              {intl.formatMessage({
                defaultMessage: 'We’re updating how you sign in',
                id: 'wFMzSOQ+Di',
              })}
            </TypographyH2>
          </DialogTitle>
          <DialogDescription asChild>
            <TypographyP2 className="text-text-secondary">
              {intl.formatMessage({
                defaultMessage:
                  'We’ve improved how you sign in. Please continue to update your login experience. Your account is unchanged.',
                id: 'SN+zMu5UAc',
              })}
            </TypographyP2>
          </DialogDescription>
        </DialogHeader>
        <div className="flex w-full flex-col items-center gap-2">
          <Button size="lg" className="w-full" onClick={handleContinue}>
            {intl.formatMessage({ defaultMessage: 'Continue', id: 'acrOozm08x' })}
          </Button>
          <Button
            size="lg"
            variant="ghost"
            className="w-full"
            onClick={handleSignInAnotherWay}
          >
            {intl.formatMessage({
              defaultMessage: 'Sign in another way',
              id: 'HusXd1Jm7K',
            })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
